import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { 
  Mountain, TrendingUp, TrendingDown, Truck, 
  Calendar, FileText, ArrowUpRight, ArrowDownRight, Edit, Save, X
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const GranulatsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editItems, setEditItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchGranulatSales();
  }, [selectedMonth, selectedYear]);

  const fetchGranulatSales = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/granulats/sales?month=${selectedMonth}&year=${selectedYear}`,
        getAuthHeaders()
      );
      setSales(response.data.sales || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching granulat sales:', error);
      toast.error('Erreur lors du chargement des ventes de granulats');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (sale) => {
    setEditingSale(sale);
    // Prepare items for editing with current costs and original index
    const items = [
      ...sale.granulat_items.map(item => ({
        ...item,
        type: 'granulat',
        purchase_cost: item.purchase_cost != null ? item.purchase_cost : '',
        description: item.description
      })),
      ...sale.transport_items.map(item => ({
        ...item,
        type: 'transport',
        purchase_cost: item.purchase_cost != null ? item.purchase_cost : '',
        description: item.description
      }))
    ];
    setEditItems(items);
    setShowEditModal(true);
  };

  const updateItemCost = (index, value) => {
    const newItems = [...editItems];
    const cost = value === '' ? '' : parseFloat(value) || 0;
    newItems[index].purchase_cost = cost;
    const numCost = cost === '' ? 0 : cost;
    newItems[index].cost_total = numCost * newItems[index].quantity;
    newItems[index].margin = newItems[index].total - newItems[index].cost_total;
    setEditItems(newItems);
  };

  const handleSaveCosts = async () => {
    setSaving(true);
    try {
      // Update the transaction with new costs
      const itemsPayload = editItems.map(item => ({
        description: item.description,
        type: item.type,
        purchase_cost: item.purchase_cost === '' ? 0 : parseFloat(item.purchase_cost) || 0
      }));
      await axios.put(
        `${API}/transactions/${editingSale.id}/update-costs`,
        { items: itemsPayload },
        getAuthHeaders()
      );
      
      toast.success('Coûts d\'achat mis à jour !');
      setShowEditModal(false);
      setEditingSale(null);
      fetchGranulatSales(); // Refresh data
    } catch (error) {
      console.error('Error updating costs:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' FCFA';
  };

  const getMarginColor = (margin) => {
    if (margin > 0) return 'text-green-600';
    if (margin < 0) return 'text-red-600';
    return 'text-slate-600';
  };

  const getMarginBadge = (margin) => {
    if (margin > 0) return <Badge className="bg-green-100 text-green-800">+{formatCurrency(margin)}</Badge>;
    if (margin < 0) return <Badge className="bg-red-100 text-red-800">{formatCurrency(margin)}</Badge>;
    return <Badge className="bg-slate-100 text-slate-800">{formatCurrency(margin)}</Badge>;
  };

  // Calculate totals from edit items
  const calculateEditTotals = () => {
    let granulatRevenue = 0, granulatCost = 0, transportRevenue = 0, transportCost = 0;
    editItems.forEach(item => {
      if (item.type === 'granulat') {
        granulatRevenue += item.total || 0;
        granulatCost += item.cost_total || 0;
      } else if (item.type === 'transport') {
        transportRevenue += item.total || 0;
        transportCost += item.cost_total || 0;
      }
    });
    return {
      granulatRevenue,
      granulatCost,
      granulatMargin: granulatRevenue - granulatCost,
      transportRevenue,
      transportCost,
      transportMargin: transportRevenue - transportCost,
      totalMargin: (granulatRevenue - granulatCost) + (transportRevenue - transportCost)
    };
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full">Chargement...</div>;
  }

  const editTotals = calculateEditTotals();

  return (
    <div className="space-y-6" data-testid="granulats-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Mountain className="w-10 h-10" />
            Ventes de Granulats
          </h1>
          <p className="text-sm text-slate-600 mt-1">Suivi des ventes et des marges sur granulats et transport</p>
        </div>
      </div>

      {/* Month/Year Filter */}
      <Card className="p-4 bg-white border border-slate-200 rounded-sm">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label>Mois:</Label>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label>Année:</Label>
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto text-sm text-slate-500">
            {summary.count || 0} vente(s) ce mois
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Granulats Revenue */}
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white p-5 rounded-sm" data-testid="granulat-revenue-card">
          <div className="flex items-center justify-between mb-2">
            <Mountain className="w-8 h-8 opacity-80" />
            <TrendingUp className="w-5 h-5 opacity-60" />
          </div>
          <p className="text-sm opacity-80">CA Granulats</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.granulat_revenue)}</p>
        </Card>

        {/* Granulats Cost */}
        <Card className="bg-gradient-to-br from-slate-600 to-slate-700 text-white p-5 rounded-sm" data-testid="granulat-cost-card">
          <div className="flex items-center justify-between mb-2">
            <ArrowDownRight className="w-8 h-8 opacity-80" />
            <TrendingDown className="w-5 h-5 opacity-60" />
          </div>
          <p className="text-sm opacity-80">Coût d'achat Granulats</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.granulat_cost)}</p>
        </Card>

        {/* Granulats Margin */}
        <Card className={`p-5 rounded-sm ${(summary.granulat_margin || 0) >= 0 ? 'bg-gradient-to-br from-green-500 to-green-600' : 'bg-gradient-to-br from-red-500 to-red-600'} text-white`} data-testid="granulat-margin-card">
          <div className="flex items-center justify-between mb-2">
            <ArrowUpRight className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-sm opacity-80">Marge Granulats</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.granulat_margin)}</p>
        </Card>

        {/* Transport Margin */}
        <Card className={`p-5 rounded-sm ${(summary.transport_margin || 0) >= 0 ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-gradient-to-br from-red-500 to-red-600'} text-white`} data-testid="transport-margin-card">
          <div className="flex items-center justify-between mb-2">
            <Truck className="w-8 h-8 opacity-80" />
          </div>
          <p className="text-sm opacity-80">Marge Transport</p>
          <p className="text-2xl font-bold">{formatCurrency(summary.transport_margin)}</p>
        </Card>
      </div>

      {/* Total Margin Banner */}
      <Card className={`p-6 rounded-sm border-2 ${(summary.total_margin || 0) >= 0 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-lg font-medium text-slate-700">Marge Totale (Granulats + Transport)</p>
            <p className="text-sm text-slate-500">Pour {MONTHS[selectedMonth-1]} {selectedYear}</p>
          </div>
          <p className={`text-4xl font-bold ${getMarginColor(summary.total_margin)}`}>
            {formatCurrency(summary.total_margin)}
          </p>
        </div>
      </Card>

      {/* Sales Table */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-sm overflow-hidden" data-testid="granulat-sales-table">
        <div className="border-b border-slate-100 p-4 bg-slate-50/50">
          <h3 className="text-xl font-semibold text-[#1B3268]">Détail des Ventes</h3>
          <p className="text-sm text-slate-500 mt-1">Cliquez sur "Modifier" pour saisir les coûts d'achat</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-semibold uppercase text-xs tracking-wider">
                <th className="p-4 text-left">Date</th>
                <th className="p-4 text-left">Référence</th>
                <th className="p-4 text-left">Client</th>
                <th className="p-4 text-left">Chantier</th>
                <th className="p-4 text-right">CA Granulats</th>
                <th className="p-4 text-right">Coût Granulats</th>
                <th className="p-4 text-right">Marge Granulats</th>
                <th className="p-4 text-right">Marge Transport</th>
                <th className="p-4 text-right">Marge Totale</th>
                <th className="p-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.map((sale) => (
                <tr key={sale.id} className="hover:bg-slate-50 transition-colors border-b last:border-0" data-testid={`granulat-sale-${sale.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">
                        {new Date(sale.date).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="font-mono text-sm">{sale.reference}</span>
                      <Badge variant="outline" className={sale.type === 'facture' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}>
                        {sale.type === 'facture' ? 'Facture' : 'BL'}
                      </Badge>
                    </div>
                  </td>
                  <td className="p-4 font-medium">{sale.client_name}</td>
                  <td className="p-4 text-slate-600">{sale.chantier || '-'}</td>
                  <td className="p-4 text-right font-mono">{formatCurrency(sale.granulat_revenue)}</td>
                  <td className="p-4 text-right font-mono text-slate-500">
                    {sale.cost_set ? formatCurrency(sale.granulat_cost) : 
                      <span className="text-amber-500 text-sm">À saisir</span>
                    }
                  </td>
                  <td className="p-4 text-right">
                    {sale.cost_set ? getMarginBadge(sale.granulat_margin) : '-'}
                  </td>
                  <td className="p-4 text-right">
                    {sale.transport_margin !== 0 ? getMarginBadge(sale.transport_margin) : '-'}
                  </td>
                  <td className="p-4 text-right">
                    <span className={`text-lg font-bold ${getMarginColor(sale.total_margin)}`}>
                      {sale.cost_set || sale.transport_cost > 0 ? formatCurrency(sale.total_margin) : '-'}
                    </span>
                  </td>
                  <td className="p-4 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEditModal(sale)}
                      className="border-amber-500 text-amber-600 hover:bg-amber-50"
                      data-testid={`edit-costs-${sale.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Coûts
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {sales.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Mountain className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>Aucune vente de granulats pour {MONTHS[selectedMonth-1]} {selectedYear}</p>
              <p className="text-sm mt-2">Créez une vente avec le type "Granulat" dans la page Ventes</p>
            </div>
          )}
        </div>
      </Card>

      {/* Info Box */}
      <Card className="p-4 bg-amber-50 border border-amber-200 rounded-sm">
        <div className="flex gap-3">
          <Mountain className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Comment ça marche ?</p>
            <ul className="mt-2 space-y-1 list-disc list-inside">
              <li>Les ventes avec produits "Granulat" ou "Transport" apparaissent ici automatiquement</li>
              <li>Cliquez sur <strong>"Coûts"</strong> pour saisir le coût d'achat de chaque ligne</li>
              <li>La marge = Prix de vente - Coût d'achat</li>
              <li>Les marges sont calculées automatiquement après saisie</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Edit Costs Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-[700px]" data-testid="edit-costs-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#1B3268] flex items-center gap-2">
              <Edit className="w-6 h-6" />
              Modifier les Coûts d'Achat
            </DialogTitle>
            <DialogDescription>
              {editingSale && (
                <span>
                  {editingSale.reference} - {editingSale.client_name}
                  {editingSale.chantier && ` - ${editingSale.chantier}`}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
            {editItems.map((item, index) => (
              <div key={index} className="border border-slate-200 rounded-md p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    {item.type === 'granulat' ? 
                      <Mountain className="w-5 h-5 text-amber-600" /> : 
                      <Truck className="w-5 h-5 text-blue-600" />
                    }
                    <span className="font-semibold">
                      {item.type === 'granulat' ? 'Granulat' : 'Transport'}
                    </span>
                    <Badge variant="outline">{item.description || 'Sans description'}</Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-5 gap-4 text-sm">
                  <div>
                    <Label className="text-xs text-slate-500">Quantité</Label>
                    <p className="font-mono font-medium">{item.quantity} {item.unit}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Prix vente unit.</Label>
                    <p className="font-mono">{formatCurrency(item.unit_price)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Total vente</Label>
                    <p className="font-mono font-bold text-[#1B3268]">{formatCurrency(item.total)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-amber-600 font-semibold">Coût achat unit. *</Label>
                    <Input
                      type="number"
                      value={item.purchase_cost !== null && item.purchase_cost !== undefined ? item.purchase_cost : ''}
                      onChange={(e) => updateItemCost(index, e.target.value)}
                      placeholder="0"
                      className="h-9 border-amber-300 focus:border-amber-500"
                      data-testid={`cost-input-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Marge</Label>
                    <p className={`font-mono font-bold ${getMarginColor(item.margin)}`}>
                      {formatCurrency(item.margin)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Totals Summary */}
          <div className="border-t pt-4 space-y-2">
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="bg-amber-50 p-3 rounded-md">
                <p className="text-amber-700 font-medium">Marge Granulats</p>
                <p className={`text-xl font-bold ${getMarginColor(editTotals.granulatMargin)}`}>
                  {formatCurrency(editTotals.granulatMargin)}
                </p>
              </div>
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-blue-700 font-medium">Marge Transport</p>
                <p className={`text-xl font-bold ${getMarginColor(editTotals.transportMargin)}`}>
                  {formatCurrency(editTotals.transportMargin)}
                </p>
              </div>
              <div className={`p-3 rounded-md ${editTotals.totalMargin >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className={editTotals.totalMargin >= 0 ? 'text-green-700' : 'text-red-700'} style={{fontWeight: 500}}>Marge Totale</p>
                <p className={`text-xl font-bold ${getMarginColor(editTotals.totalMargin)}`}>
                  {formatCurrency(editTotals.totalMargin)}
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowEditModal(false);
                setEditingSale(null);
              }}
            >
              <X className="w-4 h-4 mr-1" />
              Annuler
            </Button>
            <Button
              onClick={handleSaveCosts}
              disabled={saving}
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              <Save className="w-4 h-4 mr-1" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GranulatsPage;
